// Who the firm's mail comes from. Lives in the gear on the Email row of
// /settings/integrations.
//
// Two transports, and the form shows only what the chosen one uses: Gmail
// sends as the connected Google account and refuses a From address it does
// not own, so offering the firm a from-address there would be offering a
// field that does nothing.

import { useState } from "react";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { isAdminRole } from "../../lib/roles";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import {
  useMailConfig,
  useSaveMailConfig,
  useRemoveMailConfig,
  useTestMailConfig,
} from "../../hooks/useMailConfig";
import type {
  MailConfigStatus,
  MailSettingsUpdate,
  MailTransport,
  StaffMfaPolicy,
} from "../../api/integrations";
import { SECRET_ON_FILE } from "./secretOnFile";

type TextField = "host" | "port" | "user" | "fromAddress" | "fromName" | "replyTo" | "password";

const SMTP_FIELDS: Array<{ field: TextField; label: string; hint?: string; type?: string }> = [
  { field: "host", label: "Server", hint: "For example smtp.gmail.com or smtp.office365.com." },
  { field: "port", label: "Port", hint: "587 for STARTTLS, 465 for implicit TLS.", type: "number" },
  { field: "user", label: "Username", hint: "Usually the full mailbox address." },
  { field: "password", label: "Password", type: "password" },
  {
    field: "fromAddress",
    label: "From address",
    hint: "What the recipient sees the mail come from.",
  },
  { field: "fromName", label: "From name" },
];

function statusLine(s: MailConfigStatus): string {
  // A leftover password that no longer decrypts stops SMTP and nothing
  // else, so a firm back on Gmail must not be told its sender is broken -
  // least of all with an instruction it has no field to follow.
  if (s.transport === "gmail-oauth") {
    return "Mail goes out through the firm's connected Google account.";
  }
  if (s.unreadable) {
    return "The stored password no longer decrypts - the server encryption key changed. Enter it again.";
  }
  return s.missing.length > 0
    ? `Not ready to send - still needs ${s.missing.join(", ")}.`
    : "Mail goes out through the firm's own mail server.";
}

const POLICY_HINTS: Record<StaffMfaPolicy, string> = {
  "required-for-password-accounts":
    "Password sign-ins finish with a code: from the connected app, or mailed to staff without one. Google sign-ins are never challenged, and the mailed challenge waits for a proven sender.",
  optional: "Only staff who connected an authenticator app are asked for a code.",
  off: "No second step. Connected apps stay enrolled and come back when the policy does.",
};

// A gate token from the sender, in the words of someone who has to fix it.
const GATE_REASONS: Record<string, string> = {
  "oauth-not-configured": "Google is not connected on this server.",
  "not-authorized": "The Google grant is gone - reconnect Google.",
  "smtp-password-unreadable": "The stored password no longer decrypts.",
};

function failureLine(error: string): string {
  const known = GATE_REASONS[error];
  if (known) return known;
  return error.startsWith("smtp-incomplete:")
    ? `The settings are incomplete - no ${error.slice("smtp-incomplete:".length).trim()}.`
    : error;
}

export function MailSection() {
  // Mounted from the admin-only gear, but the flag stands on its own:
  // wherever this section lands, a non-admin never issues the GET.
  const user = useCurrentUser();
  const configQ = useMailConfig(isAdminRole(user?.role));
  const save = useSaveMailConfig();
  const remove = useRemoveMailConfig();
  const test = useTestMailConfig();
  const [edits, setEdits] = useState<
    Partial<
      Record<
        | TextField
        | "secure"
        | "transport"
        | "requireVerifiedEmail"
        | "mfaPolicy"
        | "mfaEmailAfterApp",
        string
      >
    >
  >({});

  const status = configQ.data;
  const transport = (edits.transport as MailTransport) ?? status?.transport ?? "gmail-oauth";
  const stored = (f: TextField) => (f === "password" ? "" : String(status?.[f] ?? ""));
  const value = (f: TextField) => edits[f] ?? stored(f);
  // Implicit TLS: the admin's choice, else what the port implies while they
  // are typing one, else what the server holds - which is already the
  // stored value or its own derivation from the stored port.
  const secure =
    edits.secure !== undefined
      ? edits.secure === "on"
      : edits.port !== undefined
        ? Number(value("port")) === 465
        : (status?.secure ?? false);
  const requireVerified =
    edits.requireVerifiedEmail !== undefined
      ? edits.requireVerifiedEmail === "on"
      : (status?.requireVerifiedEmail ?? true);
  const mfaPolicy =
    (edits.mfaPolicy as StaffMfaPolicy | undefined) ??
    status?.mfaPolicy ??
    "required-for-password-accounts";
  const mfaEmailAfterApp =
    edits.mfaEmailAfterApp !== undefined
      ? edits.mfaEmailAfterApp === "on"
      : (status?.mfaEmailAfterApp ?? false);

  // Only what differs from what is stored. Re-saving a value unchanged is
  // not free here: the server drops the firm's proof that mail works
  // whenever a delivery field is written, so an idle Save would close the
  // gate that proof opens.
  const update: MailSettingsUpdate = {};
  if (status) {
    if (edits.transport && edits.transport !== status.transport) {
      update.transport = edits.transport as MailTransport;
    }
    for (const f of ["host", "user", "fromAddress", "fromName", "replyTo"] as const) {
      const next = edits[f]?.trim();
      // An emptied field clears the two the sender can do without; on the
      // ones it requires there is nothing to store and nothing to say.
      if (next === undefined || next === stored(f)) continue;
      if (next !== "" || f === "replyTo" || f === "fromName") update[f] = next;
    }
    // Never trimmed and never compared: whitespace can be part of a
    // password, and the stored one is not ours to see.
    if (edits.password) update.password = edits.password;
    const port = Number(edits.port);
    if (edits.port !== undefined && Number.isFinite(port) && port > 0 && port !== status.port) {
      update.port = port;
    }
    if (secure !== status.secure) update.secure = secure;
    if (requireVerified !== status.requireVerifiedEmail) {
      update.requireVerifiedEmail = requireVerified;
    }
    if (mfaPolicy !== status.mfaPolicy) update.mfaPolicy = mfaPolicy;
    if (mfaEmailAfterApp !== status.mfaEmailAfterApp) update.mfaEmailAfterApp = mfaEmailAfterApp;
  }
  const dirty = Object.keys(update).length > 0;
  const busy = save.isPending || remove.isPending || test.isPending;

  function set(
    field:
      | TextField
      | "secure"
      | "transport"
      | "requireVerifiedEmail"
      | "mfaPolicy"
      | "mfaEmailAfterApp",
    next: string,
  ) {
    setEdits((prev) => ({ ...prev, [field]: next }));
  }

  function onSave() {
    // The verdict below belongs to the sender that was tested. Saving may
    // well be what invalidates it, so it does not outlive the save.
    test.reset();
    save.mutate(update, { onSuccess: () => setEdits({}) });
  }

  function onRemove() {
    const ok = window.confirm(
      "Remove the mail settings? Mail goes back out through the firm's connected " +
        "Google account, and the stored SMTP password is deleted.",
    );
    if (!ok) return;
    test.reset();
    remove.mutate(undefined, { onSuccess: () => setEdits({}) });
  }

  return (
    <section className="settings-section">
      <p className="settings-sub">
        Where the firm's outgoing mail leaves from - invitations, notices and, later, password
        resets. One sender for all of it.
      </p>

      {configQ.isLoading && (
        <SkeletonGroup>
          <Skeleton width={220} />
          <Skeleton width={180} />
        </SkeletonGroup>
      )}
      {configQ.isError && (
        <p className="settings-error">Could not load the mail settings. Refresh to retry.</p>
      )}

      {status && (
        <>
          <p className="settings-sub" role="status">
            {statusLine(status)}
          </p>
          {status.lastTestOkAt ? (
            <p className="settings-hint">
              A test message was delivered {new Date(status.lastTestOkAt).toLocaleString()}.
            </p>
          ) : (
            <p className="settings-warn">
              No test message has been delivered yet - nothing has proved this sender works.
            </p>
          )}
          {status.lastError && (
            <p className="settings-error">Last failure: {failureLine(status.lastError)}</p>
          )}

          <div className="settings-grid">
            <label className="settings-field">
              <span className="settings-label">Sender</span>
              <select
                className="cc-field"
                value={transport}
                disabled={busy}
                onChange={(e) => set("transport", e.target.value)}
              >
                <option value="gmail-oauth">The firm's Google mailbox</option>
                <option value="smtp">A mail server (SMTP)</option>
              </select>
              <span className="settings-hint">
                {transport === "gmail-oauth"
                  ? "Sends as the connected Google account, which is also the address replies reach."
                  : "Any provider with an SMTP host - the firm's own domain, a hosted mailbox, or a relay."}
              </span>
            </label>

            {transport === "smtp" &&
              SMTP_FIELDS.map(({ field, label, hint, type }) => (
                <label className="settings-field" key={field}>
                  <span className="settings-label">{label}</span>
                  <input
                    className="cc-field"
                    type={type ?? "text"}
                    spellCheck={false}
                    autoComplete={type === "password" ? "new-password" : "off"}
                    placeholder={
                      field === "password" && status.passwordSet ? SECRET_ON_FILE : undefined
                    }
                    value={value(field)}
                    disabled={busy}
                    onChange={(e) => set(field, e.target.value)}
                  />
                  {field === "password" && status.passwordSet && (
                    <span className="settings-hint">
                      A password is on file - saving replaces it.
                    </span>
                  )}
                  {hint && <span className="settings-hint">{hint}</span>}
                </label>
              ))}

            {transport === "smtp" && (
              <label className="settings-field">
                <span className="settings-label">Encryption</span>
                <span className="settings-hint">
                  <input
                    type="checkbox"
                    aria-label="Implicit TLS"
                    checked={secure}
                    disabled={busy}
                    onChange={(e) => set("secure", e.target.checked ? "on" : "off")}
                  />{" "}
                  Implicit TLS from the first byte (port 465). Left off, the connection starts plain
                  and is required to upgrade before the password is sent.
                </span>
              </label>
            )}

            <label className="settings-field">
              <span className="settings-label">Reply-to</span>
              <input
                className="cc-field"
                type="text"
                spellCheck={false}
                autoComplete="off"
                placeholder="office@example.com"
                value={value("replyTo")}
                disabled={busy}
                onChange={(e) => set("replyTo", e.target.value)}
              />
              <span className="settings-hint">
                Where answers go, if that is not the sending mailbox. Leave empty for none.
              </span>
            </label>
          </div>

          <div className="settings-field">
            <span className="settings-label">Sign-in policy</span>
            <label className="settings-policy">
              <input
                type="checkbox"
                aria-label="Require a confirmed email address for password sign-in"
                checked={requireVerified}
                disabled={busy}
                onChange={(e) => set("requireVerifiedEmail", e.target.checked ? "on" : "off")}
              />
              <span className="settings-policy-switch" aria-hidden="true" />
              <span className="settings-policy-text">
                <span className="settings-policy-title">
                  Password sign-in waits for a confirmed email address
                </span>
                <span className="settings-hint">
                  Google accounts are never held, and the rule applies only once a test message has
                  proved the firm's mail.
                </span>
              </span>
            </label>

            <label className="settings-policy-select">
              <span className="settings-policy-title">Two-step sign-in</span>
              <select
                className="cc-field"
                aria-label="Two-step sign-in policy"
                value={mfaPolicy}
                disabled={busy}
                onChange={(e) => set("mfaPolicy", e.target.value)}
              >
                <option value="required-for-password-accounts">
                  Required for password sign-ins
                </option>
                <option value="optional">Only staff who connected an app</option>
                <option value="off">Off</option>
              </select>
              <span className="settings-hint">{POLICY_HINTS[mfaPolicy]}</span>
            </label>

            <label className="settings-policy">
              <input
                type="checkbox"
                aria-label="Keep the mailed code working after an app is connected"
                checked={mfaEmailAfterApp}
                disabled={busy}
                onChange={(e) => set("mfaEmailAfterApp", e.target.checked ? "on" : "off")}
              />
              <span className="settings-policy-switch" aria-hidden="true" />
              <span className="settings-policy-text">
                <span className="settings-policy-title">
                  The mailed code keeps working after an app is connected
                </span>
                <span className="settings-hint">
                  Off, an account with an app takes only the app's codes or a recovery code. On, a
                  mailed code signs it in too.
                </span>
              </span>
            </label>
          </div>

          {!status.canStore && transport === "smtp" && (
            <p className="settings-error">
              Password storage is off - set ENCRYPTION_KEY on the server to save one here.
            </p>
          )}
          {status.source === "db" && status.updatedAt && (
            <p className="settings-hint">
              Saved {new Date(status.updatedAt).toLocaleDateString()}
              {status.updatedByEmail ? ` by ${status.updatedByEmail}` : ""}
            </p>
          )}

          <div className="settings-actions">
            <button
              type="button"
              className="settings-save-btn"
              disabled={!dirty || busy}
              onClick={onSave}
            >
              {save.isPending ? "Saving..." : "Save sender"}
            </button>
            <button
              type="button"
              className="cc-link-btn"
              // A test proves what is saved, not what is typed - sending
              // one over unsaved edits would stamp the wrong sender.
              disabled={dirty || busy}
              onClick={() => test.mutate()}
              title={dirty ? "Save the changes first" : undefined}
            >
              {test.isPending ? "Sending..." : "Send a test message"}
            </button>
            {status.source === "db" && (
              <button
                type="button"
                className="cc-link-btn danger"
                disabled={busy}
                onClick={onRemove}
              >
                Remove settings
              </button>
            )}
          </div>

          {save.isError && <p className="settings-error">{(save.error as Error).message}</p>}
          {remove.isError && <p className="settings-error">{(remove.error as Error).message}</p>}
          {test.isError && <p className="settings-error">{(test.error as Error).message}</p>}
          {test.data?.ok && (
            <p className="settings-ok" role="status">
              Sent. Check your own inbox - the test goes to the address you signed in with.
            </p>
          )}
          {test.data && !test.data.ok && (
            <p className="settings-error">
              Nothing was sent. {failureLine(test.data.error ?? "unknown")}
            </p>
          )}
          {save.isSuccess && !dirty && (
            <p className="settings-ok" role="status">
              Sender saved. Send a test message to prove it works.
            </p>
          )}
        </>
      )}
    </section>
  );
}
